import { useState, useRef, useEffect, useMemo } from "react";
import type { EventPublic, EventSchoolPublic, SchoolOpponentPublic, SchoolSearchResult } from "@dugads/shared";
import * as api from "@/lib/api";
import { SCHOOL_COLORS } from "@/lib/scheduler-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Home } from "lucide-react";

interface SchoolsManagerProps {
  event: EventPublic;
  sortedSchools: EventSchoolPublic[];
  canEdit: boolean;
  orgSchools: { id: string; name: string }[];
  opponents: SchoolOpponentPublic[];
  setEvent: React.Dispatch<React.SetStateAction<EventPublic | null>>;
}

export function SchoolsManager({ event, sortedSchools, canEdit, orgSchools, opponents, setEvent }: SchoolsManagerProps) {
  const [search, setSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const [nationalResults, setNationalResults] = useState<SchoolSearchResult[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Debounced school search (directory)
  useEffect(() => {
    // 087: lower threshold from 2 to 1 so national schools appear as soon as the user types
    if (search.trim().length < 1) { setNationalResults([]); return; }
    const timer = setTimeout(() => {
      api.searchSchools(search.trim(), { limit: 10 }).then((res) => setNationalResults(res.schools)).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Existing school names/ids to exclude from results
  const existingNames = useMemo(() => new Set(event.schools.map((s) => s.name.toLowerCase())), [event.schools]);
  const existingSchoolIds = useMemo(() => new Set(event.schools.map((s) => s.school_id)), [event.schools]);

  const q = search.toLowerCase();

  // Tier 1: Dugads schools matching search
  const dugadsMatches = useMemo(() => {
    if (!q) return [];
    return orgSchools.filter((s) => !existingSchoolIds.has(s.id) && s.name.toLowerCase().includes(q));
  }, [q, orgSchools, existingSchoolIds]);

  // Tier 2: Opponent shortlist matching search
  const opponentMatches = useMemo(() => {
    if (!q) return [];
    return opponents.filter((o) =>
      !existingSchoolIds.has(o.opponent_school_id) &&
      !existingNames.has(o.opponent_name.toLowerCase()) &&
      o.opponent_name.toLowerCase().includes(q),
    );
  }, [q, opponents, existingSchoolIds, existingNames]);

  // Tier 3: All schools (from API search) excluding already shown
  const nationalMatches = useMemo(() => {
    const opponentIdSet = new Set(opponentMatches.map((o) => o.opponent_school_id));
    return nationalResults.filter((s) =>
      !existingSchoolIds.has(s.id) &&
      !existingNames.has(s.name.toLowerCase()) &&
      !opponentIdSet.has(s.id),
    );
  }, [nationalResults, existingSchoolIds, existingNames, opponentMatches]);

  const hasResults = dugadsMatches.length > 0 || opponentMatches.length > 0 || nationalMatches.length > 0;

  async function addSchool(schoolId: string) {
    setSearch(""); setShowDrop(false);
    const res = await api.addEventSchool(event.id, { school_id: schoolId });
    setEvent((prev) => prev ? { ...prev, schools: [...prev.schools, res.school] } : prev);
  }

  async function addBySchoolId(schoolId: string) {
    setSearch(""); setShowDrop(false);
    const res = await api.addEventSchool(event.id, { school_id: schoolId });
    setEvent((prev) => prev ? { ...prev, schools: [...prev.schools, res.school] } : prev);
  }

  async function remove(entryId: string) {
    setEvent((prev) => prev ? { ...prev, schools: prev.schools.filter((s) => s.id !== entryId) } : prev);
    await api.removeEventSchool(event.id, entryId);
  }

  // 114 -- send (or re-send) a fixture invitation to a participating school.
  async function invite(schoolId: string) {
    await api.inviteSchoolToEvent(event.id, schoolId);
    setEvent((prev) => prev ? {
      ...prev,
      schools: prev.schools.map((s) => s.school_id === schoolId ? { ...s, status: "invited" } : s),
    } : prev);
  }

  async function setHost(entryId: string) {
    const res = await api.updateEventSchool(event.id, entryId, { is_host: true });
    setEvent((prev) => prev ? {
      ...prev,
      schools: prev.schools.map((s) => s.id === entryId ? res.school : { ...s, is_host: false }),
    } : prev);
  }

  return (
    <div className="min-w-0 flex-1" data-region="schools">
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="shrink-0 text-sm font-medium text-muted-foreground">Schools</h3>
        {canEdit && (
          <div ref={ref} className="relative min-w-0 flex-1 max-w-xs">
            <div className="flex gap-1.5">
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setShowDrop(true); }}
                onFocus={() => { if (search.trim()) setShowDrop(true); }}
                placeholder="Add school..." className="h-7 text-xs" data-field="school-search"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (dugadsMatches.length === 1) addSchool(dugadsMatches[0].id);
                    else if (opponentMatches.length === 1 && dugadsMatches.length === 0) addBySchoolId(opponentMatches[0].opponent_school_id);
                    else if (nationalMatches.length === 1 && dugadsMatches.length === 0 && opponentMatches.length === 0) addBySchoolId(nationalMatches[0].id);
                  }
                  if (e.key === "Escape") setShowDrop(false);
                }}
              />
            </div>
            {showDrop && search.trim() && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-background shadow-md">
                {dugadsMatches.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">Your Schools</div>
                    {dugadsMatches.map((s) => (
                      <button key={s.id} type="button" onClick={() => addSchool(s.id)}
                        className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">{s.name}</button>
                    ))}
                  </>
                )}
                {opponentMatches.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">Opponents</div>
                    {opponentMatches.map((o) => (
                      <button key={o.opponent_school_id} type="button" onClick={() => addBySchoolId(o.opponent_school_id)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted">
                        <span>{o.opponent_name}</span>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{o.town ? `${o.town}` : o.province}</span>
                      </button>
                    ))}
                  </>
                )}
                {nationalMatches.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">All Schools</div>
                    {nationalMatches.map((s) => (
                      <button key={s.id} type="button" onClick={() => addBySchoolId(s.id)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted">
                        <span>{s.name}</span>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{s.town ? `${s.town}, ` : ""}{s.province}</span>
                      </button>
                    ))}
                  </>
                )}
                {!hasResults && <div className="px-3 py-2 text-sm text-muted-foreground">No matches found</div>}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sortedSchools.map((s, si) => {
          const c = SCHOOL_COLORS[si % SCHOOL_COLORS.length];
          return (
            <span key={s.id} data-item="school" data-item-id={s.id} className={`group/chip inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap ${c.bg} ${c.border} ${c.text}`}>
              {s.is_host ? (
                <span title="Host school"><Home className="h-3 w-3 shrink-0" /></span>
              ) : canEdit ? (
                <button type="button" title="Set as host" onClick={() => setHost(s.id)} data-action="set-host"
                  className="hidden shrink-0 opacity-50 hover:opacity-100 group-hover/chip:inline-flex">
                  <Home className="h-3 w-3" />
                </button>
              ) : null}
              {s.name}
              {!s.is_host && s.status === "invited" && (
                <span className="rounded-full bg-[#FAEEDA] px-1.5 py-0.5 text-[10px] font-medium text-[#633806]" data-status="invited">invited</span>
              )}
              {!s.is_host && s.status === "declined" && (
                <span className="rounded-full bg-[#FCEBEB] px-1.5 py-0.5 text-[10px] font-medium text-[#791F1F]" data-status="declined">declined</span>
              )}
              {canEdit && !s.is_host && s.status !== "invited" && (
                <button type="button" title={`Invite ${s.name}`} onClick={() => invite(s.school_id)} data-action="invite-school" className="hidden text-[10px] underline opacity-70 hover:opacity-100 group-hover/chip:inline-flex">
                  invite
                </button>
              )}
              {canEdit && (
                <button type="button" title={`Remove ${s.name}`} onClick={() => remove(s.id)} data-action="remove-school" className="hidden hover:text-destructive group-hover/chip:inline-flex">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
